import { useRef, useState } from 'react';
import { z } from 'zod';
import {
  Button,
  Caption1,
  Checkbox,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  ProgressBar,
  Text,
  Title3,
  makeStyles,
} from '@fluentui/react-components';
import {
  acceptAttribute,
  checkDeclaredUpload,
  describeAccepted,
  formatRecordedCalendarDate,
  getStepType,
  isNotReadyUpload,
  NOT_READY_UPLOAD_WORDING,
  fileCategoryLabels,
  type ConfigOf,
} from '@vigeo/shared';
import { tokens } from '@vigeo/ui';
import type { StepPortalProps, StepReviewProps, StepUiModule } from '../contract.js';

const useStyles = makeStyles({
  block: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  meta: { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  picker: { display: 'none' },
  // 2.75rem is the accessible minimum touch target, and this is a phone-first surface.
  choose: { minHeight: '2.75rem', alignSelf: 'flex-start' },
  chosen: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
});

const kernel = getStepType('document-upload');
type Config = ConfigOf<'document-upload'>;

/**
 * An in-progress draft, which is genuinely not a submission: until the bytes are staged
 * there is no `uploadId`, so the completed shape cannot describe this state. The kernel's
 * submissionSchema is erased to `z.ZodType` by the contract, so `.partial()` is unavailable
 * on it — and deriving a partial from a completed shape would be the wrong move anyway.
 */
const draftSchema = z
  .object({
    uploadId: z.string(),
    fileName: z.string(),
    expiresAt: z.string().datetime().nullable(),
  })
  .partial();
// A "not ready" draft (#232) is the shared schema's own shape, checked with the shared test —
// never a second copy of it here.


type Draft = z.infer<typeof draftSchema>;

function DocumentUploadPortal({ config, draft, onDraftChange, disabled, upload }: StepPortalProps) {
  const styles = useStyles();
  const picker = useRef<HTMLInputElement | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const parsed = kernel.configSchema.safeParse(config);
  if (!parsed.success) return <Text>This step is misconfigured. HR has been notified.</Text>;

  const settings: Config = parsed.data;
  const notReady = isNotReadyUpload(draft);
  const current = draftSchema.safeParse(notReady ? {} : draft ?? {});
  const value: Draft = current.success ? current.data : {};
  const expiresAt = value.expiresAt ?? '';

  async function choose(file: File | undefined) {
    if (file === undefined) return;
    setProblem(null);

    // Checked here so the person is told immediately rather than after waiting through an
    // upload. The SAME function runs server-side, where it is the actual gate — this call
    // is a courtesy, not a control.
    const verdict = checkDeclaredUpload(
      { contentType: file.type, byteLength: file.size },
      { acceptedTypes: settings.acceptedTypes, maxSizeMb: settings.maxSizeMb },
    );
    if (!verdict.ok) {
      setProblem(verdict.message);
      return;
    }

    setProgress(0);
    const result = await upload(file, setProgress);
    setProgress(null);

    if (!result.ok) {
      setProblem(result.message);
      return;
    }
    // A file chosen after "not ready" replaces that answer: the draft is one or the other.
    onDraftChange({ ...value, uploadId: result.uploadId, fileName: result.fileName });
  }

  const busy = disabled || progress !== null;

  return (
    <div className={styles.block}>
      <Title3>{settings.heading}</Title3>
      {settings.instructions === '' ? null : <Text>{settings.instructions}</Text>}
      {(settings.documents ?? []).map(document => <a data-document-link key={document.url} href={document.url} target="_blank" rel="noreferrer">{document.label}</a>)}
      <div className={styles.meta}>
        <Caption1>Accepted: {describeAccepted(settings.acceptedTypes)}</Caption1>
        <Caption1>Up to {settings.maxSizeMb} MB</Caption1>
        {settings.dueDate === undefined ? null : <Caption1>Please send it by {formatRecordedCalendarDate(settings.dueDate)}</Caption1>}
      </div>

      {problem === null ? null : (
        <MessageBar intent="error">
          <MessageBarBody>{problem}</MessageBarBody>
        </MessageBar>
      )}

      <input
        ref={picker}
        type="file"
        className={styles.picker}
        accept={acceptAttribute(settings.acceptedTypes)}
        // No `capture`: forcing the camera would stop someone attaching a PDF their
        // licensing board emailed them, which is how most of these documents arrive.
        onChange={(event) => {
          void choose(event.target.files?.[0]);
          // Cleared so picking the same file twice after a failure still fires a change.
          event.target.value = '';
        }}
        disabled={busy}
      />

      <Button className={styles.choose} disabled={busy} onClick={() => picker.current?.click()}>
        {value.uploadId === undefined ? 'Choose a file' : 'Choose a different file'}
      </Button>

      {progress === null ? null : (
        <ProgressBar value={progress} max={1} thickness="large" />
      )}

      {value.fileName === undefined ? null : (
        <div className={styles.chosen}>
          <Text weight="semibold">{value.fileName}</Text>
          <Caption1>Uploaded. It is not sent to us until you submit this step.</Caption1>
        </div>
      )}

      {/* #232: uploads never block onboarding. Choosing this is an answer the person SENDS;
          the requirement stays outstanding until HR's secure upload link brings the file. */}
      <Checkbox
        checked={notReady}
        disabled={busy}
        label={NOT_READY_UPLOAD_WORDING.option}
        onChange={(_, d) => onDraftChange(d.checked === true ? { notReady: true } : {})}
      />
      {notReady ? <Caption1>{NOT_READY_UPLOAD_WORDING.note}</Caption1> : null}

      {settings.collectsExpiry && !notReady ? (
        /* Marked required because the server refuses a blank one. `collectsExpiry` means
           the requirement is tracked BY this date, so a field that reads as optional
           invites a submission that is rejected — the same failure #113 fixed on the
           date-credential screen: the person does as the screen says and is refused for it. */
        <Field label="Expiry date" required hint="Exactly as printed on the document.">
          <Input
            type="date"
            value={expiresAt === '' ? '' : expiresAt.slice(0, 10)}
            disabled={busy}
            onChange={(_, d) =>
              onDraftChange({
                ...value,
                // Parsed as UTC midnight, not local. `new Date('2027-03-12')` and
                // `new Date('2027-03-12T00:00')` differ by a timezone offset, which is how
                // an expiry silently lands on the previous day west of Greenwich.
                expiresAt: d.value === '' ? null : `${d.value}T00:00:00.000Z`,
              })
            }
          />
        </Field>
      ) : null}
    </div>
  );
}

function DocumentUploadReview({ config, submission }: StepReviewProps) {
  const styles = useStyles();
  const settings = kernel.configSchema.safeParse(config);
  const answer = kernel.submissionSchema.safeParse(submission);
  const file = answer.success && !('notReady' in answer.data) ? answer.data : null;

  return (
    <div className={styles.block}>
      <Text weight="semibold">{settings.success ? settings.data.heading : 'Document'}</Text>
      {file !== null ? (
        <>
          <Text>{file.fileName}</Text>
          {file.expiresAt === null ? null : (
            <Caption1>Expires {new Date(file.expiresAt).toLocaleDateString()}</Caption1>
          )}
        </>
      ) : answer.success ? (
        <Text>{NOT_READY_UPLOAD_WORDING.hrStatus}</Text>
      ) : (
        <Text>Nothing submitted yet.</Text>
      )}
      {settings.success ? (
        // Where approving this will file it — the reviewer should know before they approve,
        // especially for a segregated destination.
        <Caption1>Files to {fileCategoryLabels[settings.data.target]}</Caption1>
      ) : null}
    </div>
  );
}

export const documentUploadUi: StepUiModule = { portal: DocumentUploadPortal, review: DocumentUploadReview };
